import {computeAllocationStatus} from './allocation'
import type {TimelineJobRow} from './job-rows'
import type {AllocationStatus, AttentionLens} from './types'

export type AttentionLensOption = {
    value: AttentionLens
    label: string
    count: number
}

export function matchesAttentionLens(job: TimelineJobRow, lens: AttentionLens): boolean {
    switch (lens) {
        case 'all':
            return true
        case 'needs_attention':
            return job.status === 'unassigned'
    }
}

export function filterJobsByLens(jobs: TimelineJobRow[], lens: AttentionLens): TimelineJobRow[] {
    return jobs.filter((job) => matchesAttentionLens(job, lens))
}

const ALLOCATION_SORT_PRIORITY: Record<AllocationStatus, number> = {
    needs_resources: 0,
    partially_allocated: 0,
    ready: 0,
}

/** Within a day column — incomplete jobs rise to the top. */
export function sortJobsWithinColumn(jobs: TimelineJobRow[]): TimelineJobRow[] {
    return [...jobs].sort((a, b) => {
        const aAttention = a.status === 'unassigned' ? 0 : 1
        const bAttention = b.status === 'unassigned' ? 0 : 1
        if (aAttention !== bAttention) return aAttention - bAttention

        if (a.status === 'unassigned' && b.status === 'unassigned') {
            const aAlloc = ALLOCATION_SORT_PRIORITY[allocationStatusForRow(a)]
            const bAlloc = ALLOCATION_SORT_PRIORITY[allocationStatusForRow(b)]
            if (aAlloc !== bAlloc) return aAlloc - bAlloc
        }

        return a.label.localeCompare(b.label)
    })
}

/** Attention jobs surface first, then scheduled jobs by day order. */
export function sortJobsForCommitmentBoard(jobs: TimelineJobRow[]): TimelineJobRow[] {
    return [...jobs].sort((a, b) => {
        const aAttention = a.status === 'unassigned' ? 0 : 1
        const bAttention = b.status === 'unassigned' ? 0 : 1
        if (aAttention !== bAttention) return aAttention - bAttention

        const aDay = a.span.dayOffset ?? 0
        const bDay = b.span.dayOffset ?? 0
        if (aDay !== bDay) return aDay - bDay

        return a.label.localeCompare(b.label)
    })
}

export type CommitmentBoardSection = {
    key: string
    label: string | null
    jobs: TimelineJobRow[]
}

/** Groups sorted jobs into needs-attention and committed sections. */
export function buildCommitmentBoardSections(
    jobs: TimelineJobRow[],
    lens: AttentionLens,
): CommitmentBoardSection[] {
    const sorted = sortJobsForCommitmentBoard(jobs)
    const needsAttention = sorted.filter((job) => job.status === 'unassigned')
    const committed = sorted.filter((job) => job.status === 'assigned')

    const sections: CommitmentBoardSection[] = []

    if (needsAttention.length > 0) {
        sections.push({
            key: 'needs_attention',
            label: lens === 'all' ? 'Needs attention' : null,
            jobs: needsAttention,
        })
    }
    if (committed.length > 0) {
        sections.push({
            key: 'committed',
            label: lens === 'all' ? 'Committed' : null,
            jobs: committed,
        })
    }

    return sections
}

export function buildAttentionLensOptions(jobs: TimelineJobRow[]): AttentionLensOption[] {
    const needsAttentionCount = jobs.filter((job) => job.status === 'unassigned').length

    return [
        {value: 'all', label: 'All jobs', count: jobs.length},
        {value: 'needs_attention', label: 'Needs attention', count: needsAttentionCount},
    ]
}

export function allocationStatusForRow(job: TimelineJobRow): AllocationStatus {
    if (job.allocationStatus) return job.allocationStatus
    if (job.unassignedJob) return computeAllocationStatus(job.unassignedJob)
    return 'needs_resources'
}
